// Blocco firma GDPR: se l'admin ha richiesto la firma, l'utente non può usare
// il portale finché non firma l'informativa (nome digitato = firma elettronica semplice).
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, LogOut, Loader2, FileSignature } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GdprGate({ children }) {
  const [status, setStatus] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [name, setName] = useState("");
  const [signing, setSigning] = useState(false);

  useEffect(() => {
    axios.get(`${API}/gdpr/status`)
      .then((r) => setStatus(r.data))
      .catch(() => setStatus({ required: false }));
  }, []);

  const sign = async () => {
    setSigning(true);
    try {
      await axios.post(`${API}/gdpr/sign`, { signed_name: name.trim() });
      toast.success("Firma registrata. Benvenuto!");
      setStatus({ required: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante la firma");
    } finally {
      setSigning(false);
    }
  };

  const exit = () => {
    ["token", "impersonating", "impersonating_name", "active_company_id", "available_companies"].forEach((k) => localStorage.removeItem(k));
    window.location.href = "/login";
  };

  if (status === null) {
    return <div className="min-h-screen bg-stone-50 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-stone-400" /></div>;
  }
  if (!status.required) return children;

  const canSign = accepted && name.trim().length >= 5 && name.trim().includes(" ");

  return (
    <div className="min-h-screen bg-stone-100 flex items-center justify-center p-3 sm:p-6" data-testid="gdpr-gate">
      <div className="bg-white border border-stone-200 rounded-xl shadow-xl w-full max-w-3xl flex flex-col max-h-[94vh]">
        <div className="bg-[#8A1C2A] text-white rounded-t-xl px-5 py-4 flex items-center gap-3 shrink-0">
          <ShieldCheck className="w-7 h-7 shrink-0" />
          <div>
            <h1 className="font-bold text-lg leading-tight">Informativa privacy e nomina GDPR</h1>
            <p className="text-xs text-white/70">Per usare il portale devi leggere e firmare il documento — richiesto da AMPIO GROUP S.R.L.</p>
          </div>
        </div>
        <div className="overflow-y-auto px-5 py-4 text-[13px] leading-relaxed text-stone-700 whitespace-pre-line border-b border-stone-200" data-testid="gdpr-text">
          {status.text}
        </div>
        <div className="px-5 py-4 space-y-3 shrink-0">
          <label className="flex items-start gap-2.5 cursor-pointer">
            <Checkbox checked={accepted} onCheckedChange={(v) => setAccepted(!!v)} className="mt-0.5" data-testid="gdpr-accept-checkbox" />
            <span className="text-sm text-stone-700">Dichiaro di aver letto e compreso l'informativa e di <b>accettare la designazione ad autorizzato al trattamento</b> (art. 29 GDPR).</span>
          </label>
          <div>
            <p className="text-xs font-semibold text-stone-600 mb-1">Firma — digita il tuo nome e cognome completi</p>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Mario Rossi" className="font-serif italic text-lg" data-testid="gdpr-sign-name" />
            <p className="text-[11px] text-stone-400 mt-1">Verranno registrati anche data, ora e indirizzo IP come estremi della firma.</p>
          </div>
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-between pt-1">
            <Button variant="outline" onClick={exit} className="text-stone-600" data-testid="gdpr-exit-btn">
              <LogOut className="w-4 h-4 mr-2" /> Esci senza firmare
            </Button>
            <Button onClick={sign} disabled={!canSign || signing} className="bg-[#8A1C2A] hover:bg-[#6e1622]" data-testid="gdpr-sign-btn">
              {signing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSignature className="w-4 h-4 mr-2" />}
              Firma e accedi
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
